import React, { useState } from 'react';
import axios from 'axios';
import { useContent } from '../i18n/LanguageContext';
import { MapPin, Mail, Phone, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const c = useContent();
  const ct = c.contact;
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', tel: '', message: '', hp: '' });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const saved = JSON.parse(localStorage.getItem('logitag_leads') || '[]');
      saved.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('logitag_leads', JSON.stringify(saved));
    } catch (_) {}
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang: c.lang });
    } catch (_) {}
    setSending(false);
    setSent(true);
    toast({ title: ct.toastTitle, description: ct.toastDesc });
    setForm({ prenom: '', nom: '', email: '', tel: '', message: '' });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="contact" className="py-14 md:py-20 bg-slate-50">
      <div className="lt-container grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
        <div className="reveal flex flex-col">
          <p className="text-sm font-semibold uppercase tracking-widest text-teal-600">{ct.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{ct.title}</h2>
          <p className="mt-4 text-lg text-slate-600 max-w-md">{ct.subtitle}</p>

          <div className="mt-10 space-y-4">
            {[
              { icon: MapPin, label: ct.labels.address, value: ct.address },
              { icon: Mail, label: ct.labels.email, value: ct.email, href: `mailto:${ct.email}` },
              { icon: Phone, label: ct.labels.phone, value: ct.phone, href: `tel:${ct.phone.replace(/[^+0-9]/g, '')}` },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200 p-5">
                <span className="grid place-items-center w-11 h-11 shrink-0 rounded-xl bg-teal-50 text-teal-600">
                  <item.icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="text-slate-800 font-medium hover:text-teal-700 transition-colors">{item.value}</a>
                  ) : (
                    <p className="text-slate-800 font-medium">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="reveal rounded-3xl bg-white border border-slate-200 shadow-xl p-7 md:p-9">
          <input
            type="text" tabIndex="-1" autoComplete="off" aria-hidden="true"
            value={form.hp} onChange={update('hp')}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={ct.labels.first} required value={form.prenom} onChange={update('prenom')} placeholder={ct.ph.first} />
            <Field label={ct.labels.last} required value={form.nom} onChange={update('nom')} placeholder={ct.ph.last} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <Field label={ct.labels.mail} type="email" required value={form.email} onChange={update('email')} placeholder={ct.ph.mail} />
            <Field label={ct.labels.tel} required value={form.tel} onChange={update('tel')} placeholder={ct.ph.tel} />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{ct.labels.message} <span className="text-teal-600">*</span></label>
            <Textarea required value={form.message} onChange={update('message')} rows={4} placeholder={ct.ph.message} className="resize-none" />
          </div>
          <Button type="submit" disabled={sending} className="btn-shine mt-6 w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold disabled:opacity-70">
            {sending ? (<><Loader2 className="mr-2 w-5 h-5 animate-spin" /> …</>) : sent ? (<><CheckCircle2 className="mr-2 w-5 h-5" /> {c.cta.sent}</>) : (<><Send className="mr-2 w-4 h-4" /> {c.cta.send}</>)}
          </Button>
          <p className="mt-3 text-xs text-center text-slate-400">{ct.privacy}</p>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-teal-600">*</span>}
      </label>
      <Input required={required} {...props} className="h-11" />
    </div>
  );
}
